import type * as FavCache from '@finn-no/favorite-api-browser-client';
import { i18n } from '@lingui/core';
import { modal as modalClasses } from '@warp-ds/css/component-classes';
import { Button, TextField } from '@warp-ds/react';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import useCreateList from './hooks/useCreateList';

export type NewFolderTemplateRef = {
    submit: () => void;
};

type NewFolderTemplateProps = {
    itemType: FavCache.ItemType;
    itemId: number;
    api: FavCache.FavoriteBrowserCache;
    closeCallback: () => void;
    initialFocusRef: React.RefObject<HTMLInputElement>;
    ref: React.Ref<NewFolderTemplateRef>;
};
const NewFolderTemplate = forwardRef(
    (props: NewFolderTemplateProps, ref: React.Ref<NewFolderTemplateRef>) => {
        const { itemType, itemId, api, closeCallback, initialFocusRef } = props;

        const [folderName, setFolderName] = useState('');
        const [errorMessage, setErrorMessage] = useState('');

        const createList = useCreateList({
            api,
            itemType,
            itemId,
            setErrorMessage,
            closeCallback,
        });

        async function onSubmitFolder() {
            if (!folderName.trim()) {
                if (initialFocusRef.current) {
                    initialFocusRef.current.focus();
                }
                setShouldValidate(true);
                return;
            }

            return createList(folderName);
        }

        useImperativeHandle(ref, () => ({ submit: () => onSubmitFolder() }));

        // Validation feedback shouldn't be shown until after the user have interacted with the input
        const [shouldValidate, setShouldValidate] = useState(false);

        const inputField = (
            <TextField
                invalid={shouldValidate && folderName.trim().length < 1}
                helpText={i18n.t({
                    id: 'listNameHelpText',
                    message: '{length} / 40 characters',
                    values: { length: folderName.length },
                })}
                label={i18n.t({
                    id: 'listNameLabel',
                    message: 'Give the list a name',
                })}
                maxLength={40}
                onChange={(event) => setFolderName(event.target.value)}
                ref={initialFocusRef}
                required
                value={folderName}
            />
        );

        return (
            <form
                className="pb-8"
                onSubmit={(event) => {
                    event.preventDefault();
                    onSubmitFolder();
                }}
            >
                {inputField}
                {errorMessage && (
                    <p className="s-text-negative" role="alert">
                        {errorMessage}
                    </p>
                )}
            </form>
        );
    },
);

type ModalContentProps = {
    onCancel: () => void;
    onSubmit: () => void;
};
export const ModalContent: (
    props: NewFolderTemplateProps & ModalContentProps,
) => [JSX.Element, JSX.Element, JSX.Element] = (
    props: NewFolderTemplateProps & ModalContentProps,
) => {
    const {
        itemType,
        itemId,
        api,
        initialFocusRef,
        closeCallback,
        onCancel,
        onSubmit,
    } = props;

    const footer = (
        <>
            <Button secondary quiet onClick={onCancel} className="mr-12">
                {i18n.t({
                    id: 'backButton',
                    message: 'Back',
                })}
            </Button>
            <Button primary onClick={onSubmit}>
                {i18n.t({
                    id: 'addInAList',
                    message: 'Add to list',
                })}
            </Button>
        </>
    );

    const body: JSX.Element = (
        <>
            <NewFolderTemplate
                itemType={itemType}
                itemId={itemId}
                api={api}
                initialFocusRef={initialFocusRef}
                closeCallback={closeCallback}
                ref={props.ref}
            />
        </>
    );
    return [
        body,
        footer,
        <h1 className={modalClasses.titleText}>
            {i18n.t({
                id: 'newList',
                message: 'New list',
            })}
        </h1>,
    ];
};
